import type { Bulletin } from '@shared/types/Bulletin';
import type { Salarie } from '@shared/types/Salarie';
import type { Tenant } from '@shared/types/Tenant';
import { roundFCFA } from '@shared/utils/round';
import { ventilationCnssDuBulletin } from '@domain/paie/VentilationCnss';

export type TypeDeclaration = 'cnss' | 'cnamgs' | 'dgi';

/** Colonnes d'identification communes, précédant les montants de chaque bordereau. */
const NB_COLONNES_IDENTITE = 3;

/**
 * Bordereaux CNSS, CNAMGS et DGI/ID10 de la période, au format CSV.
 *
 * Le fichier est un bordereau destiné à l'organisme, pas un simple export du
 * tableau : il porte un en-tête employeur, puis l'état nominatif identifiant
 * chaque salarié par son matricule et son numéro auprès de l'organisme, et
 * enfin une ligne de totaux alignée sur les indicateurs de l'écran.
 * Séparateur `;` et montants entiers en FCFA (tableur fr-FR).
 */
export class DeclarationsExporter {
  static readonly SEPARATEUR = ';';

  static readonly LIBELLES: Record<TypeDeclaration, string> = {
    cnss: 'CNSS',
    cnamgs: 'CNAMGS',
    dgi: 'DGI',
  };

  static readonly COLONNES: Record<TypeDeclaration, readonly string[]> = {
    cnss: [
      'Matricule',
      'Salarié',
      'N° CNSS',
      'Base plaf.',
      'Prestations familiales (emp.)',
      'AT-MP (emp.)',
      'VID (emp.)',
      'Patronale',
      'Salariale',
      'Total CNSS',
    ],
    cnamgs: ['Matricule', 'Salarié', 'N° CNAMGS', 'Base plaf.', 'Patronale', 'Salariale'],
    dgi: ['Matricule', 'Salarié', 'NIF', 'IRPP', 'TCS', 'CFP (emp.)', 'FNH (emp.)'],
  };

  /** Numéro identifiant le salarié auprès de l'organisme destinataire. */
  static bulletinEligible(b: Bulletin): boolean {
    return b.statut === 'valide' || b.statut === 'paye';
  }

  static identifiant(type: TypeDeclaration, salarie: Salarie | undefined): string {
    if (!salarie) return '';
    if (type === 'cnss') return salarie.numero_cnss || salarie.cnss || '';
    if (type === 'cnamgs') return salarie.cnamgs || '';
    return salarie.nif || '';
  }

  /**
   * Montants du bordereau CNSS d'un bulletin : base plafonnée, CNSS patronale ventilée (prestations familiales 5 %,
   * AT-MP 2 %, VID 11 %), total patronal (18 %), part salariale (VID 5 %) et total CNSS (23 %).
   */
  static montantsCnss(b: Bulletin): number[] {
    const v = ventilationCnssDuBulletin(b);
    return [b.base_cnss, v.pf, v.atmp, v.vid, v.total, b.cnss_vid_sal, v.total + b.cnss_vid_sal];
  }

  /** Déclaration nominative CNSS de la période. */
  static genererCNSS(
    bulletins: readonly Bulletin[],
    salaries: readonly Salarie[],
    periode: string,
    entreprise?: Tenant | null,
  ): string {
    return this.construire('cnss', bulletins, salaries, periode, entreprise, (b) =>
      this.montantsCnss(b),
    );
  }

  /** Cotisations CNAMGS de la période. */
  static genererCNAMGS(
    bulletins: readonly Bulletin[],
    salaries: readonly Salarie[],
    periode: string,
    entreprise?: Tenant | null,
  ): string {
    return this.construire('cnamgs', bulletins, salaries, periode, entreprise, (b) => [
      b.base_cnamgs,
      b.cnamgs_emp + b.cnamgs_fam_emp,
      b.cnamgs_sal,
    ]);
  }

  /** Déclaration DGI ID10 (IRPP + TCS + CFP + FNH) de la période. */
  static genererDGI(
    bulletins: readonly Bulletin[],
    salaries: readonly Salarie[],
    periode: string,
    entreprise?: Tenant | null,
  ): string {
    return this.construire('dgi', bulletins, salaries, periode, entreprise, (b) => [
      b.irpp_mensuel,
      b.tcs,
      b.cfp_emp,
      b.fnh_emp,
    ]);
  }

  /** Aiguillage générique utilisé par les boutons d'export de l'écran. */
  static genererCSV(
    type: TypeDeclaration,
    bulletins: readonly Bulletin[],
    salaries: readonly Salarie[],
    periode: string,
    entreprise?: Tenant | null,
  ): string {
    if (type === 'cnss') return this.genererCNSS(bulletins, salaries, periode, entreprise);
    if (type === 'cnamgs') return this.genererCNAMGS(bulletins, salaries, periode, entreprise);
    return this.genererDGI(bulletins, salaries, periode, entreprise);
  }

  /** Nom de fichier lisible et déterministe : `declaration_cnss_2026-06.csv`. */
  static nomFichier(type: TypeDeclaration, periode: string): string {
    return `declaration_${type}_${periode}.csv`;
  }

  private static construire(
    type: TypeDeclaration,
    bulletins: readonly Bulletin[],
    salaries: readonly Salarie[],
    periode: string,
    entreprise: Tenant | null | undefined,
    montants: (b: Bulletin) => number[],
  ): string {
    const retenus = bulletins.filter((b) => b.periode === periode && this.bulletinEligible(b));
    const parId = new Map(salaries.map((s) => [s.id, s]));
    const colonnes = this.COLONNES[type];
    const lignes = this.enteteEmployeur(type, periode, entreprise, retenus.length);
    lignes.push(colonnes.join(this.SEPARATEUR));

    const totaux = new Array<number>(colonnes.length - NB_COLONNES_IDENTITE).fill(0);
    for (const b of retenus) {
      const salarie = parId.get(b.salarie_id);
      const valeurs = montants(b).map((v) => roundFCFA(v || 0));
      valeurs.forEach((v, i) => {
        totaux[i] = (totaux[i] ?? 0) + v;
      });
      const identite = [
        b.matricule || salarie?.matricule || '',
        this.nomSalarie(b, salarie),
        this.identifiant(type, salarie),
      ].map((v) => this.echapper(v));
      lignes.push([...identite, ...valeurs].join(this.SEPARATEUR));
    }
    if (retenus.length > 0) {
      const label = ['TOTAL', ...new Array<string>(NB_COLONNES_IDENTITE - 1).fill('')];
      lignes.push([...label, ...totaux].join(this.SEPARATEUR));
    }
    return lignes.join('\n');
  }

  /** Cartouche employeur attendu en tête de bordereau, suivi d'une ligne vide. */
  private static enteteEmployeur(
    type: TypeDeclaration,
    periode: string,
    entreprise: Tenant | null | undefined,
    effectif: number,
  ): string[] {
    const paires: Array<[string, string]> = [
      ['Déclaration', this.LIBELLES[type]],
      ['Période', periode],
      ['Employeur', entreprise?.raison_sociale || entreprise?.nom || ''],
      ['NIF', entreprise?.nif || ''],
      ['RCCM', entreprise?.rccm || ''],
      ['N° CNSS employeur', entreprise?.parametres?.numero_cnss_employeur || ''],
      ['N° CNAMGS employeur', entreprise?.parametres?.numero_cnamgs_employeur || ''],
      ['Effectif déclaré', String(effectif)],
    ];
    return [
      ...paires.map(([cle, valeur]) => [cle, this.echapper(valeur)].join(this.SEPARATEUR)),
      '',
    ];
  }

  /** Même résolution de libellé que l'écran : bulletin, puis salarié, puis matricule. */
  private static nomSalarie(b: Bulletin, salarie: Salarie | undefined): string {
    const depuisFiche = salarie ? `${salarie.prenom} ${salarie.nom}` : '';
    return b.nom_complet || depuisFiche || b.matricule || '—';
  }

  /** Échappement CSV : guillemets doublés si séparateur, guillemet ou saut de ligne. */
  private static echapper(value: string): string {
    return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  }
}
